import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subscription, timer, TimeoutError, firstValueFrom } from 'rxjs';
import { isString, isArray, isNumber } from '../../models/utils';
import { DeferredPromise } from '../../models/deferred-promise';
import { DrWsRequestMessage, RequestMessagePromiseManager } from '../../models/dr-request-info';
import { DrWsRequestMessageStatus, Endpoint, MessageType, UpdateStatusType, EntityType } from '../../models/enums';
import { OperationState, Task, TaskStatus } from '../../models/task';
import { CommonService } from '../common/common.service';
import * as _ from 'underscore';

@Injectable()
export class HttpService {
    subscription: boolean;
    endpoint: Endpoint;
    showErrorModal: boolean;

    constructor(private http: HttpClient, private commonService: CommonService) {
        this.updateStatusTypes = this.commonService.getEnumNames(UpdateStatusType);
        this.organizationUpdateStatusTypes = [UpdateStatusType.PROJECT, UpdateStatusType.ADMIN_REQUEST, UpdateStatusType.TASK, UpdateStatusType.STORAGE];
    }

    private readonly updateStatusTypes: Array<string>;
    private readonly organizationUpdateStatusTypes: Array<string>;
    private firstPollForSearch: boolean;
    private sessionExpireInProgress: boolean;

    private getUpdateStatusTimer: {
        subscription: Subscription,
        interval: number,
        timestamp: number
    };
    private getTasksTimer: {
        subscription: Subscription,
        activeCall: Promise<any>,
        tasks: Array<{ handle: string, callback: (task: Task) => void }>,
        interval: number,
        counter: number
    };
    private getUpdateSystemStatusTimer: {
        subscription: Subscription,
        interval: number,
        timestamp: number
    };

    get(fullUrl: string): Observable<any> {  // This can go other places besides the typical /eDiscovery/[Endpoint]
        return this.http.get(fullUrl, { responseType: 'text' });
    }

    postWithObservable(url: Endpoint, data: any, showErrorModal: boolean): Observable<any> {
        this.showErrorModal = showErrorModal ? showErrorModal : true;
        data = JSON.stringify(new DrWsRequestMessage(data, this.commonService.contextHandle));
        return this.http.post(
            (this.commonService.serverUrl + url),
            data,
            {
                headers: new HttpHeaders().set('Authorization', this.commonService.sessionToken).append('Content-Type', 'application/json')
            });
    }

    // messageParams = true will return immediately and still poll
    // messageParams = array will return immediately and send update message with these params when task completes
    post(url: Endpoint, data: any, showErrorModal = true, messageParams: boolean | [MessageType, UpdateStatusType] = false, customAuthHeader?: string): Promise<any> {
        const request = new DeferredPromise<any>();
        const requestData = JSON.stringify(new DrWsRequestMessage(data, this.commonService.contextHandle));
        const existingRequest = RequestMessagePromiseManager.Instance.getRequest(url, requestData);
        if (existingRequest) {
            return existingRequest.promise.promise;
        } else {
            RequestMessagePromiseManager.Instance.addRequest(url, requestData, request);
        }
        const authHeader = customAuthHeader ? customAuthHeader : this.commonService.sessionToken;
        firstValueFrom(this.http.post<any>(
            this.commonService.serverUrl + url,
            requestData,
            {
                // HttpHeaders is immutable and so MUST use the constructor or the decorator pattern to be set.
                // headers = new HttpHeaders();  headers.set(...); will SILENTLY FAIL
                headers: new HttpHeaders({
                    Authorization: authHeader,
                    'Content-Type': 'application/json'
                })

            }
        ), { defaultValue: undefined }).then(response => {
            if (response) {
                if (response.taskHandle) {
                    this.listTasks(url);
                }
                if (response.status === DrWsRequestMessageStatus.FAILURE && url !== Endpoint.taskManager_getTasks) {
                    if (showErrorModal && (response.extendedStatus === null ||
                        ((response.extendedStatus?.includes('Domain List (') && response.extendedStatus?.includes('is empty.')) && this.commonService?.userSelectionClick) ||
                        (!response.extendedStatus?.includes('Domain List (') && !response.extendedStatus?.includes('is empty.')))
                    ) {
                        this.commonService.selectedErrorNavigationNode = this.commonService.selectedNavigationNode;
                        this.commonService.openErrorModal(response.extendedStatus);
                    }
                    request.reject(response);
                } else {
                    if (this.commonService.isExternalApp && !this.getUpdateStatusTimer) {
                        // external application: start poller manually after first api call
                        this.startPoller();
                    }
                    if (url !== Endpoint.projectManager_getUpdateStatus &&
                        url !== Endpoint.taskManager_getTasks &&
                        url !== Endpoint.viewManager_getReport &&
                        url !== Endpoint.projectManager_getProjectResourceStatus &&
                        url !== Endpoint.realmManager_listSystemUserProjectsByUserName) {
                        // set token only if not polling
                        if (!customAuthHeader && isString(response.sessionToken)) {
                            this.commonService.sessionToken = response.sessionToken;
                        }
                    }
                    if (response.workbasketHandle && !response.taskHandle) {
                        response.taskHandle = response.workbasketHandle;
                    }

                    const isGetReportWithBlockedPolling = url === Endpoint.viewManager_getReport && !response.requestHandle;
                    const pollingBlocked = url === Endpoint.workFlowManager_processWorkFlow || url === Endpoint.realmManager_updateVirusDefinitions || isGetReportWithBlockedPolling;
                    if (response.taskHandle && !pollingBlocked) {
                        if (this.isSearchApi(url)) {
                            this.firstPollForSearch = true;
                        }
                        if (url === Endpoint.viewManager_tagDocuments && this.commonService.documentViewerOpenContext) {
                            this.commonService.isTagTaskInProgress = true;
                        }
                        this.pollForTask(response.taskHandle, (task: Task) => {
                            if (task && task.handle === response.taskHandle) {
                                if (task.isTimeoutTask) {
                                    request.reject(task);
                                }
                                if (task.taskStatus === TaskStatus.FAILURE) {
                                    if (showErrorModal) {
                                        this.commonService.openErrorModal(task.extendedStatus);
                                    }
                                    request.reject(task);
                                    if (url === Endpoint.corpusManager_createRepresentation) {
                                        this.commonService.sendMessage(MessageType.clearPendingNavigation, UpdateStatusType.CORPUS);
                                    }
                                    if (this.isSearchApi(url)) {
                                        // search task complete => refresh search history
                                        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.RESULT_VIEW);
                                    }
                                    if (task.task === 'PREPARE_FOR_ANALYTICS') {
                                        // create cluster failed
                                        this.commonService.sendMessage(MessageType.refreshCluster, task);
                                    }
                                } else if (task.taskStatus === TaskStatus.IN_PROGRESS) {
                                    if (this.isSearchApi(url) && this.firstPollForSearch) {
                                        this.searchNavigationAndRefresh(response);
                                    }
                                    if (url === Endpoint.viewManager_search && response.viewHandle) {
                                        // send event in order to select the search from tree
                                    } else {
                                        // send event with task.percentComplete
                                    }
                                } else if (task.taskStatus === TaskStatus.SUCCESS) {
                                    if (url === Endpoint.viewManager_listDocuments) {
                                        // call listdocs again to get the doc list since the task is finished
                                        this.post(url, data, showErrorModal).then((listDocumentsResponse) => {
                                            request.resolve(listDocumentsResponse);
                                        }, listDocumentsError => {
                                            request.reject(listDocumentsError);
                                        });
                                    } else if (url === Endpoint.orgManager_importProject && response.project) {
                                        request.resolve(response);
                                    } else if (task.task === 'PREPARE_FOR_ANALYTICS') {
                                        if (task.operationState === OperationState.RUNNING) {
                                            // creating subclusters in progress
                                            request.resolve(task);
                                        } else {
                                            // create cluster complete
                                            this.commonService.sendMessage(MessageType.refreshCluster, task);
                                            request.resolve(response);
                                        }
                                    } else if (url === Endpoint.corpusManager_listImportExceptions || url === Endpoint.viewManager_getReport) {
                                        this.commonService.sendMessage(MessageType.reloadExceptionDetailsListAfterPollComplete);
                                        request.resolve({});
                                    } else if (url === Endpoint.ecaManager_createExportStreamAndPreviewExport && response.entityHandle) {
                                        request.resolve(response);
                                    } else if (url === Endpoint.projectManager_getMetadataValues) {
                                        this.commonService.sendMessage(MessageType.getMetadataValuesAfterPollComplete);
                                        request.resolve(response);
                                    } else {
                                        if (isArray(messageParams)) {
                                            if (url === Endpoint.projectManager_removeCorpusFromProject || url === Endpoint.ecaManager_deleteCustodian) {
                                                this.commonService.sendMessage(messageParams[0],
                                                    { isDeleteOperation: true, updateStatusType: messageParams[1] });
                                            } else {
                                                this.commonService.sendMessage(messageParams[0],
                                                    messageParams[1] === UpdateStatusType.IMPORT_PROJECT ? response : messageParams[1]);
                                            }
                                        } else {
                                            request.resolve(task);
                                            if (this.isSearchApi(url)) {
                                                const handle = task.handle;
                                                this.commonService.sendMessage(MessageType.entityChanged, {
                                                    updateStatusType: UpdateStatusType.SEARCH,
                                                    updateTypeAttributes: {
                                                        SEARCH: {
                                                            handle: [
                                                                {
                                                                    name: 'wbhandle',
                                                                    handle: handle
                                                                },
                                                                {
                                                                    name: 'username',
                                                                    value: task.owner
                                                                }
                                                            ]
                                                        }
                                                    }
                                                });
                                                if (!this.firstPollForSearch) {
                                                    // search task complete => refresh search history
                                                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.RESULT_VIEW);
                                                } else {
                                                    this.searchNavigationAndRefresh(response);
                                                }
                                            }
                                        }
                                    }
                                } else {
                                    request.resolve(false);
                                }
                            }
                        });
                        if (messageParams) { // pollForTask will send out an update message.
                            // resolve now with the original call's response (not the task's final result)
                            request.resolve(response);
                        }
                    } else {
                        if (messageParams && isArray(messageParams)) {
                            this.commonService.sendMessage(messageParams[0], messageParams[1]);
                        }
                        request.resolve(response);
                    }
                }
            } else {
                if (messageParams && isArray(messageParams)) {
                    this.commonService.sendMessage(messageParams[0], messageParams[1]);
                }
                if (url === Endpoint.storageAreaManager_countCustomersForFacility && isNumber(response)) {
                    request.resolve(response);
                } else {
                    request.resolve({});
                }
            }
            RequestMessagePromiseManager.Instance.removeRequest(url, requestData);
        }).catch(error => {
            if (showErrorModal) {
                this.commonService.openErrorModal(error);
            }
            request.reject(error);
            RequestMessagePromiseManager.Instance.removeRequest(url, requestData);
        });
        return request.promise;
    }

    searchNavigationAndRefresh(response: any): void {
        this.firstPollForSearch = false;
        // refresh one time when polling on the tasks start
        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.RESULT_VIEW);
        if (response) {
            // navigate to in progress search result
            this.commonService.sendMessage(MessageType.navigationRequest, {
                handle: response.taskHandle, entityType: EntityType.searchResult
            });
        }
    }

    listTasks(endpoint: Endpoint): void {
        this.endpoint = endpoint;
        if (this.commonService.currentProject) {
            this.post(Endpoint.projectManager_listTasks, {
                count: 100,
                projectHandle: this.commonService.currentProject.handle,
                selectedAttributes: ['includesavedsearches'],
                filters: this.commonService.currentWorkbasketFilters ? this.commonService.currentWorkbasketFilters : []
            }).then(response => {
                this.commonService.sendMessage(MessageType.entityChanged, {
                    updateStatusType: UpdateStatusType.TASK,
                    response: response
                });  // Send to workbasket
            });
        }
    }

    isSearchApi(url: string): boolean {
        if (url === Endpoint.viewManager_search || url === Endpoint.viewManager_createSampleSet
            || url === Endpoint.viewManager_bulkSearch || url === Endpoint.viewManager_metaDataBulkSearch
            || url === Endpoint.viewManager_searchByDoc || url === Endpoint.viewManager_searchByDocuments
            || url === Endpoint.viewManager_searchByView
            || url === Endpoint.viewManager_searchForDuplicateDocuments || url === Endpoint.viewManager_searchForDuplicateGroups
            || url === Endpoint.viewManager_exportErrorsMetaDataBulkSearch) {
            return true;
        }
        return false;
    }
    startPoller(getUpdateStatusInterval?: number): void {
        this.getUpdateStatusTimer = {
            subscription: null,
            interval: getUpdateStatusInterval ? getUpdateStatusInterval : 30000,
            timestamp: 0
        };
        this.getUpdateStatusTimerSet();
    }

    startSystemStatusPoller(): void {
        this.getUpdateSystemStatusTimer = {
            subscription: null,
            interval: 60000,   // 1 minute
            timestamp: 0
        };
        this.getUpdateSystemStatusTimerSet();
    }

    stopPoller(): void {
        if (this.getUpdateStatusTimer && this.getUpdateStatusTimer.subscription) {
            this.getUpdateStatusTimer.subscription.unsubscribe();
        }
        if (this.getTasksTimer && this.getTasksTimer.subscription) {
            this.getTasksTimer.subscription.unsubscribe();
        }
    }

    stopSystemStatusPoller(): void {
        if (!this.commonService.currentProject && this.getTasksTimer?.tasks) {
            // user navigate to home page => clear task array to skip api ECASL-35492
            this.getTasksTimer.tasks.length = 0;
        }
        if (this.getUpdateSystemStatusTimer && this.getUpdateSystemStatusTimer.subscription) {
            this.getUpdateSystemStatusTimer.subscription.unsubscribe();
        }
    }

    getUpdateStatusTimerSet(): void {
        if (this.getUpdateStatusTimer) {
            this.getUpdateStatusTimer.subscription = timer(this.getUpdateStatusTimer.interval).subscribe(() => this.getUpdateStatus());
        }
    }

    getUpdateSystemStatusTimerSet(): void {
        if (this.getUpdateSystemStatusTimer) {
            this.getUpdateSystemStatusTimer.subscription = timer(this.getUpdateSystemStatusTimer.interval).subscribe(() => this.getUpdateStatus(true));
        }
    }

    getUpdateStatus(isForSystemStatus = false): void {
        if (this.commonService.sessionToken === 'bogus:bogus') {
            // user logged out, skip api
            return;
        }
        let updateStatusTypes = isForSystemStatus ? [UpdateStatusType.CONNECTOR, UpdateStatusType.COMPONENT, UpdateStatusType.STORAGE] :
            (this.commonService.currentProject ? this.updateStatusTypes : this.organizationUpdateStatusTypes);
        const clientOnlyTypes = [ // These are only recognized in the UI and will cause an error if sent.
            UpdateStatusType.CUSTOM_FIELD,
            UpdateStatusType.EXCLUDED_CONTENT,
            UpdateStatusType.EXPORT_DATABASE,
            UpdateStatusType.KEY_FILE,
            UpdateStatusType.IMPORT_PROJECT,
            UpdateStatusType.PATTERN,
            UpdateStatusType.USER,
            UpdateStatusType.BULKSEARCH_IN_SEARCH_HISTORY,
            UpdateStatusType.REALM_NODE_VIEW,
            UpdateStatusType.SERVICE
        ];
        updateStatusTypes = _.without(updateStatusTypes, ...clientOnlyTypes);
        this.post(Endpoint.projectManager_getUpdateStatus, {
            projectHandle: this.commonService.currentProject && !isForSystemStatus ? this.commonService.currentProject.handle : 0,
            timestamp: isForSystemStatus ? this.getUpdateSystemStatusTimer.timestamp : this.getUpdateStatusTimer.timestamp,
            updateStatusTypes: updateStatusTypes
        }).then(response => {
            if (response) {
                if (response.sessionExpirationWarningTime > 0 && !this.commonService.isExternalApp && !this.sessionExpireInProgress) {
                    this.showSessionExpiredWarning(response.sessionExpirationWarningTime);
                    return;
                }
                if (response.updateTypes) {
                    response.updateTypes.forEach((element: string) => {
                        if (UpdateStatusType[element]) {
                            const updateType = UpdateStatusType[element] as UpdateStatusType;
                            if (updateType === UpdateStatusType.SEARCH) {
                                this.commonService.sendMessage(MessageType.entityChanged, {
                                    updateStatusType: updateType,
                                    updateTypeAttributes: response.updateTypeAttributes
                                });
                            } else {
                                this.commonService.sendMessage(MessageType.entityChanged, updateType);
                            }
                            if (isForSystemStatus && (updateType === UpdateStatusType.CONNECTOR || updateType === UpdateStatusType.COMPONENT || updateType === UpdateStatusType.STORAGE)) {
                                this.commonService.sendMessage(MessageType.updateProjectMonitoringStatus);
                            }
                            if (updateType === UpdateStatusType.STORAGE) {
                                this.commonService.sendMessage(MessageType.updateStoragesList);
                            }
                        }
                    });
                    const resultVIewUpdateType = updateStatusTypes.filter((type) => (type === UpdateStatusType.RESULT_VIEW));
                    if (this.commonService.selectedErrorNavigationNode && resultVIewUpdateType.length > 0 &&
                        this.commonService.selectedNavigationNode?.entityType && this.commonService.selectedNavigationNode.entityType !== EntityType.searchResult) {
                        this.commonService.selectedErrorNavigationNode = null;
                    }
                }
                if (response.timestamp) {
                    if (isForSystemStatus) {
                        this.getUpdateSystemStatusTimer.timestamp = response.timestamp;
                    } else {
                        this.getUpdateStatusTimer.timestamp = response.timestamp;
                    }

                }
                if (isForSystemStatus) {
                    this.getUpdateSystemStatusTimerSet();
                } else {
                    this.getUpdateStatusTimerSet();
                }
            }
        });
    }

    showSessionExpiredWarning(expirationTime: number) {
        this.sessionExpireInProgress = true;
        this.stopPoller();
        this.stopSystemStatusPoller();
        this.commonService.openWarningWithCounterModal('You will be logged out due to inactivity in ', 'Session Expiration Warning', 'Stay Logged In', expirationTime).then((modalRes) => {
            this.sessionExpireInProgress = false;
            if (modalRes?.modalResult) {
                this.post(Endpoint.realmManager_resetSessionInactivityTimeout, {}, false).then(() => {
                    this.startPoller();
                    this.startSystemStatusPoller();
                }, (rejected) => {
                    this.commonService.clearData();
                    this.commonService.navigateToLoginAndNotifyChildWindows();
                    if (this.commonService.projectName) {
                        this.post(Endpoint.projectManager_closeProject, { handle: this.commonService.projectName });
                    }
                    this.commonService.openErrorModal('The current session expired! Please log in again.');
                });
            } else {
                if (modalRes.modalData) {
                    // timeout used in order to delay handling of session expiration until warning modal is closed
                    setTimeout(() => {
                        if (this.commonService.projectName) {
                            this.post(Endpoint.projectManager_closeProject, { handle: this.commonService.projectName });
                        }
                        this.commonService.openErrorModal('The current session expired! Please log in again.');
                    }, 100);
                }
            }
        });
    }

    getTasksTimerSet(): void {
        if (this.getTasksTimer && this.getTasksTimer.tasks.length) {
            this.getTasksTimer.subscription = timer(this.getTasksTimer.interval).subscribe(() => this.getTasks());
        }
    }

    getTasks(): void {
        if (this.commonService.sessionToken === 'bogus:bogus') {
            // user logged out, clear task array to skip api
            this.getTasksTimer.tasks.length = 0;
        }
        if (this.getTasksTimer.tasks.length) {
            this.getTasksTimer.activeCall = this.post(Endpoint.taskManager_getTasks, { taskHandles: this.getTasksTimer.tasks.map(task => task.handle) }).then(response => {
                this.getTasksTimer.activeCall = null;
                if (response && response.tasks && response.tasks.length > 0) {
                    const tasks = (response.tasks as Array<Task>).map(t => new Task(t));
                    tasks.forEach(task => {
                        if (task.task === 'TERM_SEARCH_HIT_COUNT') {
                            const isReportDownloaded = this.commonService.termSearchHitCountReportsDownloaded?.find(report => report?.handle === task?.handle);
                            if (isReportDownloaded && task.taskStatus === TaskStatus.SUCCESS) {
                                this.commonService.sendMessage(MessageType.enableReportDownload, task);
                            }
                        }
                        const index = this.getTasksTimer.tasks.findIndex(t => t.handle === task.handle);
                        if (index >= 0) {
                            this.getTasksTimer.tasks[index].callback(task);
                            if (task.taskStatus !== TaskStatus.IN_PROGRESS) {
                                this.getTasksTimer.tasks.splice(index, 1);
                            }
                        }
                    });
                }
                if (this.getTasksTimer.tasks.length) {
                    if (this.getTasksTimer.counter <= 3) {
                        this.getTasksTimer.interval = 1000;
                    } else if (this.getTasksTimer.counter <= 5) {
                        this.getTasksTimer.interval = 2000;
                    } else if (this.getTasksTimer.counter <= 10) {
                        this.getTasksTimer.interval = 3000;
                    } else {
                        this.getTasksTimer.interval = 10000;
                    }
                    this.getTasksTimer.counter++;
                    this.getTasksTimerSet();
                }
            }, error => {
                if (error instanceof TimeoutError) {
                    this.getTasksTimer.tasks.forEach(task => {
                        const newTimeoutTask = new Task();
                        newTimeoutTask.handle = task.handle;
                        newTimeoutTask.isTimeoutTask = true;
                        task.callback(newTimeoutTask);
                    });
                }
            });
        }
    }

    pollForTask(handle: string, callback: any): void {
        if (typeof handle === 'string' && typeof callback === 'function') {
            if (!this.getTasksTimer) {
                this.getTasksTimer = {
                    subscription: null,
                    activeCall: null,
                    tasks: [],
                    interval: 10000,
                    counter: 1
                };
            }
            this.getTasksTimer.tasks.push({ handle: handle, callback: callback });
            this.getTasksTimer.counter = 1;
            this.getTasksTimer.interval = 1000;
            if (!this.getTasksTimer.activeCall) {
                this.getTasksTimerSet();
            }
        }
    }
}
